import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  CheckCircle2,
  Download,
  Eye,
  FileText,
  ListChecks,
  Loader2,
  MapPin,
  RotateCcw,
  Search,
  X,
} from 'lucide-react';

import api from '../../../../services/api';
import { getApiErrorMessage } from '../../../../utils/apiError';
import Layout from '../components/layout/Layout.tsx';

type KanbanStatus =
  | 'iniciado'
  | 'em_preparo'
  | 'em_transporte'
  | 'finalizado'
  | 'cancelado';

interface SubItem {
  descricao?: string;
  nome?: string;
  valorTotal?: number | string;
  valor_total?: number | string;
  preco?: number | string;
}

interface Item {
  quantidade?: number | string;
  nome?: string;
  produto?: string;
  descricao?: string;
  subItens?: SubItem[];
  adicionais?: SubItem[];
  observacao?: string;
  valorTotal?: number | string;
  valor_total?: number | string;
  total?: number | string;
  preco?: number | string;
  valorUnitario?: number | string;
  valor_unitario?: number | string;
}

type AddressFieldValue = string | number | null | undefined | Record<string, unknown>;

interface Pessoa {
  nome?: string;
  telefone?: string;
  endereco?: AddressFieldValue;
  numero?: AddressFieldValue;
  complemento?: AddressFieldValue;
  bairro?: AddressFieldValue;
  cidade?: AddressFieldValue;
  uf?: AddressFieldValue;
  estado?: AddressFieldValue;
  cep?: AddressFieldValue;
}

interface PedidoData {
  id?: string | number;
  idUnicoIntegracao?: string | number;
  order_id?: string | number;
  pessoa?: Pessoa;
  tipo_pagamento?: string;
  forma_pagamento?: string;
  tipoDelivery?: string;
  valorTotal?: number | string;
  valor_total?: number | string;
  total?: number | string;
  valorTaxaEntrega?: number | string;
  taxa_entrega?: number | string;
  desconto?: number | string;
  troco?: number | string;
  descricao?: string;
  observacao?: string;
  itens?: Item[];
  cancelamento_motivo?: string;
}

interface Pedido {
  id: string | number;
  status: string;
  criado_em: string;
  em_preparo_em?: string | null;
  em_transporte_em?: string | null;
  finalizado_em?: string | null;
  cancelado_em?: string | null;
  cliente_id?: string | number;
  pedido?: PedidoData;
  atendabot_clientes?: {
    nome?: string;
    telefone?: string;
  };
}

type PedidoWithStatus = Pedido & {
  kanbanStatus: KanbanStatus;
};

type SummaryCard = {
  label: string;
  value: string;
  valueClass?: string;
};

const TIME_ZONE = 'America/Sao_Paulo';
const PAGE_SIZE = 12;

const RAW_STATUS_TO_KANBAN: Record<string, KanbanStatus> = {
  iniciado: 'iniciado',
  created: 'iniciado',
  waiting: 'iniciado',
  processing: 'iniciado',
  pending_confirmation: 'iniciado',
  processing_confirmation: 'iniciado',
  em_preparo: 'em_preparo',
  confirmed: 'em_preparo',
  confirmado: 'em_preparo',
  ready_to_deliver: 'em_preparo',
  em_transporte: 'em_transporte',
  dispatched: 'em_transporte',
  finalizado: 'finalizado',
  done: 'finalizado',
  concluded: 'finalizado',
  cancelado: 'cancelado',
  canceled: 'cancelado',
  cancelled: 'cancelado',
  failed: 'cancelado',
  pending_cancelled: 'cancelado',
};

const STATUS_LABELS: Record<KanbanStatus, string> = {
  iniciado: 'Iniciado',
  em_preparo: 'Em preparo',
  em_transporte: 'Em transporte',
  finalizado: 'Finalizado',
  cancelado: 'Cancelado',
};

const STATUS_BADGE_CLASSES: Record<KanbanStatus, string> = {
  iniciado: 'whitespace-nowrap bg-amber-50 text-amber-700 border border-amber-200',
  em_preparo: 'whitespace-nowrap bg-blue-50 text-blue-700 border border-blue-200',
  em_transporte: 'whitespace-nowrap bg-indigo-50 text-indigo-700 border border-indigo-200',
  finalizado: 'whitespace-nowrap bg-emerald-50 text-emerald-700 border border-emerald-200',
  cancelado: 'whitespace-nowrap bg-rose-50 text-rose-700 border border-rose-200',
};

const DELIVERY_BADGE_CLASSES: Record<'delivery' | 'retirada', string> = {
  delivery: 'bg-blue-50 text-blue-700 border border-blue-200',
  retirada: 'bg-teal-50 text-teal-700 border border-teal-200',
};

const PAYMENT_OPTIONS = [
  { value: 'all', label: 'Todos' },
  { value: 'pix', label: 'Pix' },
  { value: 'credito', label: 'Crédito' },
  { value: 'debito', label: 'Débito' },
  { value: 'dinheiro', label: 'Dinheiro' },
  { value: 'outros', label: 'Outros' },
];

const STATUS_OPTIONS = [
  { value: 'all', label: 'Todos' },
  { value: 'iniciado', label: 'Iniciado' },
  { value: 'em_preparo', label: 'Em preparo' },
  { value: 'em_transporte', label: 'Em transporte' },
  { value: 'finalizado', label: 'Finalizado' },
  { value: 'cancelado', label: 'Cancelado' },
];

function formatDateInput(date: Date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function getCurrentMonthStart() {
  const date = new Date();
  date.setDate(1);
  return formatDateInput(date);
}

function getTodayDateStr() {
  return formatDateInput(new Date());
}

function normalizeText(value?: string | number | null) {
  return String(value ?? '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

function normalizeKey(value?: string | number | null) {
  return normalizeText(value)
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

function normalizeStatus(status: string): KanbanStatus {
  return RAW_STATUS_TO_KANBAN[normalizeKey(status)] || 'iniciado';
}

function toNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value !== 'string') return null;

  const sanitized = value.trim().replace(/\./g, '').replace(',', '.');
  if (!sanitized) return null;

  const parsed = Number(sanitized);
  return Number.isFinite(parsed) ? parsed : null;
}

function formatCurrency(value: unknown) {
  const numeric = toNumber(value);
  if (numeric === null) return '-';

  return numeric.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });
}

function formatCurrencyFromNumber(value: number) {
  return value.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });
}

function getPedidoTotalNumber(pedido: Pedido) {
  return toNumber(
    pedido?.pedido?.valorTotal
      ?? pedido?.pedido?.valor_total
      ?? pedido?.pedido?.total
  ) ?? 0;
}

function getPedidoItems(pedido: Pedido): Item[] {
  return Array.isArray(pedido?.pedido?.itens) ? pedido.pedido!.itens! : [];
}

function getItemName(item?: Item) {
  return item?.nome || item?.produto || item?.descricao || 'Item sem nome';
}

function getItemQuantity(item?: Item) {
  return toNumber(item?.quantidade) ?? 1;
}

function getItemTotal(item?: Item) {
  const directTotal = toNumber(
    item?.valorTotal
      ?? item?.valor_total
      ?? item?.total
  );
  if (directTotal !== null) return directTotal;

  const unit = toNumber(item?.preco ?? item?.valorUnitario ?? item?.valor_unitario);
  if (unit === null) return null;
  return unit * getItemQuantity(item);
}

function getPedidoItemsCount(pedido: Pedido) {
  const items = getPedidoItems(pedido);
  if (!items.length) return 0;
  return items.reduce((total, item) => total + getItemQuantity(item), 0);
}

function getPedidoDisplayId(pedido: Pedido) {
  return String(
    pedido?.pedido?.idUnicoIntegracao
      ?? pedido?.pedido?.order_id
      ?? pedido?.pedido?.id
      ?? pedido?.id
      ?? ''
  ).trim();
}

function getClienteNome(pedido: Pedido) {
  return (
    pedido?.pedido?.pessoa?.nome
    || pedido?.atendabot_clientes?.nome
    || 'Cliente'
  );
}

function getClienteTelefone(pedido: Pedido) {
  return (
    pedido?.pedido?.pessoa?.telefone
    || pedido?.atendabot_clientes?.telefone
    || ''
  );
}

function formatPhone(phone?: string) {
  if (!phone) return '-';
  const digits = phone.replace(/\D/g, '');
  if (!digits) return phone;

  if (digits.startsWith('55') && digits.length >= 12) {
    const area = digits.slice(2, 4);
    const rest = digits.slice(4);
    const prefix = rest.length >= 9 ? rest.slice(0, 5) : rest.slice(0, 4);
    const suffix = rest.length >= 9 ? rest.slice(5, 9) : rest.slice(4, 8);
    return `(${area}) ${prefix}-${suffix}`;
  }

  if (digits.length >= 10) {
    const area = digits.slice(0, 2);
    const rest = digits.slice(2);
    const prefix = rest.length >= 9 ? rest.slice(0, 5) : rest.slice(0, 4);
    const suffix = rest.length >= 9 ? rest.slice(5, 9) : rest.slice(4, 8);
    return `(${area}) ${prefix}-${suffix}`;
  }

  return phone;
}

function getDateParts(dateValue?: string) {
  const date = new Date(dateValue || '');
  if (Number.isNaN(date.getTime())) {
    return {
      date: '-',
      time: '-',
      longDate: '-',
      shortDateTime: '-',
    };
  }

  return {
    date: date.toLocaleDateString('pt-BR', {
      timeZone: TIME_ZONE,
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    }),
    time: date.toLocaleTimeString('pt-BR', {
      timeZone: TIME_ZONE,
      hour: '2-digit',
      minute: '2-digit',
    }),
    longDate: date.toLocaleDateString('pt-BR', {
      timeZone: TIME_ZONE,
      weekday: 'long',
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    }),
    shortDateTime: date.toLocaleString('pt-BR', {
      timeZone: TIME_ZONE,
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    }),
  };
}

function getTimeMinutes(dateValue?: string) {
  const date = new Date(dateValue || '');
  if (Number.isNaN(date.getTime())) return null;

  const parts = new Intl.DateTimeFormat('pt-BR', {
    timeZone: TIME_ZONE,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(date);

  const hour = Number(parts.find((part) => part.type === 'hour')?.value || '0');
  const minute = Number(parts.find((part) => part.type === 'minute')?.value || '0');
  if (!Number.isFinite(hour) || !Number.isFinite(minute)) return null;
  return (hour % 24) * 60 + minute;
}

function timeInputToMinutes(value: string) {
  if (!value) return null;
  const [hour, minute] = value.split(':').map(Number);
  if (!Number.isFinite(hour) || !Number.isFinite(minute)) return null;
  return hour * 60 + minute;
}

function getDeliveryType(pedido: Pedido): 'delivery' | 'retirada' {
  const normalized = normalizeKey(pedido?.pedido?.tipoDelivery).toUpperCase();
  const retiradaValues = ['RETIRAR', 'RETIRADA', 'PICKUP', 'TAKEOUT', 'BALCAO', 'RETIRADA_BALCAO'];
  return retiradaValues.includes(normalized) ? 'retirada' : 'delivery';
}

function getTipoEntregaLabel(pedido: Pedido) {
  return getDeliveryType(pedido) === 'retirada' ? 'Retirada' : 'Delivery';
}

function getPaymentRaw(pedido: Pedido) {
  return pedido?.pedido?.tipo_pagamento || pedido?.pedido?.forma_pagamento || '';
}

function getPaymentLabel(pedido: Pedido) {
  const raw = String(getPaymentRaw(pedido) || '').trim();
  if (!raw) return '-';

  const key = normalizeKey(raw);
  if (key.includes('pix')) return 'Pix';
  if (key.includes('credito') || key.includes('credit')) return 'Crédito';
  if (key.includes('debito') || key.includes('debit')) return 'Débito';
  if (key.includes('dinheiro') || key.includes('cash')) return 'Dinheiro';
  return raw;
}

function getPaymentFilterKey(pedido: Pedido) {
  const key = normalizeKey(getPaymentRaw(pedido));
  if (!key) return 'outros';
  if (key.includes('pix')) return 'pix';
  if (key.includes('credito') || key.includes('credit')) return 'credito';
  if (key.includes('debito') || key.includes('debit')) return 'debito';
  if (key.includes('dinheiro') || key.includes('cash')) return 'dinheiro';
  return 'outros';
}

function getSearchText(pedido: Pedido) {
  const itemsText = getPedidoItems(pedido)
    .map((item) => [
      getItemName(item),
      item?.observacao,
      ...(item?.subItens || []).map((sub) => sub.nome || sub.descricao || ''),
      ...(item?.adicionais || []).map((sub) => sub.nome || sub.descricao || ''),
    ].join(' '))
    .join(' ');

  return normalizeText([
    getPedidoDisplayId(pedido),
    getClienteNome(pedido),
    getClienteTelefone(pedido),
    getPaymentLabel(pedido),
    getTipoEntregaLabel(pedido),
    itemsText,
  ].join(' '));
}

function getAddressFieldText(
  value: unknown,
  preferredKeys: string[] = [
    'nome',
    'name',
    'descricao',
    'description',
    'label',
    'titulo',
    'title',
    'valor',
    'value',
    'sigla',
    'uf',
  ]
): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string' || typeof value === 'number') {
    return String(value).trim();
  }
  if (Array.isArray(value)) {
    return value
      .map((item) => getAddressFieldText(item, preferredKeys))
      .filter(Boolean)
      .join(' ');
  }
  if (typeof value !== 'object') return '';

  const record = value as Record<string, unknown>;
  for (const key of preferredKeys) {
    const text = getAddressFieldText(record[key], preferredKeys);
    if (text) return text;
  }

  return '';
}

function getAddressLines(pessoa?: Pessoa) {
  if (!pessoa) return { primary: '', secondary: '' };

  const street = [
    getAddressFieldText(pessoa.endereco, ['logradouro', 'street', 'endereco', 'nome', 'name', 'descricao']),
    getAddressFieldText(pessoa.numero),
  ].filter(Boolean).join(', ');
  const primary = [
    street,
    getAddressFieldText(pessoa.complemento),
  ].filter(Boolean).join(' - ');
  const state = getAddressFieldText(pessoa.uf, ['sigla', 'uf', 'nome', 'name'])
    || getAddressFieldText(pessoa.estado, ['sigla', 'uf', 'nome', 'name', 'estado']);
  const city = [
    getAddressFieldText(pessoa.cidade, ['nome', 'name', 'cidade', 'city', 'descricao']),
    state,
  ].filter(Boolean).join(' - ');
  const secondary = [
    getAddressFieldText(pessoa.bairro, ['nome', 'name', 'bairro', 'neighborhood']),
    city,
    getAddressFieldText(pessoa.cep),
  ].filter(Boolean).join(', ');

  return { primary, secondary };
}

function buildCsvValue(value: unknown) {
  const raw = String(value ?? '');
  return `"${raw.replace(/"/g, '""')}"`;
}

function downloadFile(filename: string, content: string, type: string) {
  const blob = new Blob([content], { type });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

function getTimelineSteps(pedido: PedidoWithStatus) {
  if (pedido.kanbanStatus === 'cancelado') {
    return ['iniciado', 'cancelado'] as KanbanStatus[];
  }

  return getDeliveryType(pedido) === 'retirada'
    ? (['iniciado', 'em_preparo', 'finalizado'] as KanbanStatus[])
    : (['iniciado', 'em_preparo', 'em_transporte', 'finalizado'] as KanbanStatus[]);
}

function getPedidoStatusTimestamp(pedido: PedidoWithStatus, status: KanbanStatus) {
  if (status === 'iniciado') return pedido.criado_em;
  if (status === 'em_preparo') return pedido.em_preparo_em;
  if (status === 'em_transporte') return pedido.em_transporte_em;
  if (status === 'finalizado') return pedido.finalizado_em;
  if (status === 'cancelado') return pedido.cancelado_em;
  return null;
}

function getTimelineTimestampLabel(
  pedido: PedidoWithStatus,
  status: KanbanStatus,
  isDone: boolean
) {
  const timestamp = getPedidoStatusTimestamp(pedido, status);
  if (!timestamp) return isDone ? 'Sem registro' : 'Aguardando';

  const parts = getDateParts(timestamp);
  if (parts.date === '-' || parts.time === '-') return isDone ? 'Sem registro' : 'Aguardando';
  return `${parts.date} ${parts.time}`;
}

function PedidoDetailModal({
  pedido,
  onClose,
}: {
  pedido: PedidoWithStatus;
  onClose: () => void;
}) {
  const displayId = getPedidoDisplayId(pedido);
  const dateParts = getDateParts(pedido.criado_em);
  const entrega = getDeliveryType(pedido);
  const endereco = getAddressLines(pedido?.pedido?.pessoa);
  const itens = getPedidoItems(pedido);
  const total = pedido?.pedido?.valorTotal ?? pedido?.pedido?.valor_total ?? pedido?.pedido?.total;
  const taxaEntrega = pedido?.pedido?.valorTaxaEntrega ?? pedido?.pedido?.taxa_entrega;
  const desconto = pedido?.pedido?.desconto;
  const troco = pedido?.pedido?.troco;
  const statusSteps = getTimelineSteps(pedido);
  const currentStepIndex = statusSteps.indexOf(pedido.kanbanStatus);

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-950/45 px-4 py-6 backdrop-blur-sm">
      <div className="flex max-h-[92vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl">
        <div className="flex items-center justify-between gap-4 border-b border-slate-100 px-5 py-4">
          <div className="flex min-w-0 items-center gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-indigo-50 text-indigo-600">
              <ListChecks size={20} />
            </div>
            <div className="min-w-0">
              <h3 className="truncate text-base font-semibold text-slate-900">
                Pedido {displayId ? `#${displayId}` : ''}
              </h3>
              <p className="truncate text-xs text-slate-500">
                {dateParts.time} - {dateParts.longDate}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-slate-200 bg-slate-50 text-slate-500 transition hover:bg-slate-100 hover:text-slate-700"
            aria-label="Fechar detalhes do pedido"
          >
            <X size={16} />
          </button>
        </div>

        <div className="space-y-5 overflow-y-auto px-5 py-5">
          <section>
            <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">Cliente</p>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="rounded-xl border border-slate-100 bg-slate-50 px-4 py-3">
                <p className="text-xs text-slate-400">Nome</p>
                <p className="mt-1 truncate text-sm font-semibold text-slate-900">{getClienteNome(pedido)}</p>
              </div>
              <div className="rounded-xl border border-slate-100 bg-slate-50 px-4 py-3">
                <p className="text-xs text-slate-400">Telefone</p>
                <p className="mt-1 truncate text-sm font-semibold text-slate-900">{formatPhone(getClienteTelefone(pedido))}</p>
              </div>
            </div>
          </section>

          <section>
            <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">Resumo</p>
            <div className="grid gap-3 sm:grid-cols-3">
              <div className="rounded-xl border border-slate-100 bg-slate-50 px-4 py-3">
                <p className="text-xs text-slate-400">Tipo de entrega</p>
                <span className={`mt-2 inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${DELIVERY_BADGE_CLASSES[entrega]}`}>
                  {getTipoEntregaLabel(pedido)}
                </span>
              </div>
              <div className="rounded-xl border border-slate-100 bg-slate-50 px-4 py-3">
                <p className="text-xs text-slate-400">Pagamento</p>
                <p className="mt-2 truncate text-sm font-semibold text-slate-900">{getPaymentLabel(pedido)}</p>
              </div>
              <div className="rounded-xl border border-slate-100 bg-slate-50 px-4 py-3">
                <p className="text-xs text-slate-400">Status</p>
                <span className={`mt-2 inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${STATUS_BADGE_CLASSES[pedido.kanbanStatus]}`}>
                  {STATUS_LABELS[pedido.kanbanStatus]}
                </span>
              </div>
            </div>
          </section>

          <section>
            <p className="mb-3 text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">Progresso do pedido</p>
            <div className="flex items-start">
              {statusSteps.map((status, index) => {
                const isCancelado = status === 'cancelado';
                const done = isCancelado || currentStepIndex < 0 || index <= currentStepIndex;
                return (
                  <React.Fragment key={status}>
                    <div className="flex min-w-[64px] flex-1 flex-col items-center">
                      <div className={`flex h-7 w-7 items-center justify-center rounded-full text-[11px] font-semibold ${
                        isCancelado
                          ? 'bg-rose-500 text-white'
                          : done
                          ? 'bg-indigo-600 text-white'
                          : 'bg-slate-100 text-slate-400'
                      }`}
                      >
                        {done ? <CheckCircle2 size={14} /> : index + 1}
                      </div>
                      <p className={`mt-2 text-center text-[11px] font-semibold ${
                        isCancelado ? 'text-rose-600' : done ? 'text-indigo-600' : 'text-slate-400'
                      }`}
                      >
                        {STATUS_LABELS[status]}
                      </p>
                      <p className={`mt-1 text-center text-[10px] leading-4 ${
                        done ? 'text-slate-500' : 'text-slate-400'
                      }`}
                      >
                        {getTimelineTimestampLabel(pedido, status, done)}
                      </p>
                    </div>
                    {index < statusSteps.length - 1 && (
                      <div className={`mt-3 h-0.5 flex-1 ${done ? 'bg-indigo-600' : 'bg-slate-200'}`} />
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          </section>

          {entrega === 'delivery' && (endereco.primary || endereco.secondary) && (
            <section>
              <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">Endereço de entrega</p>
              <div className="flex items-start gap-3 rounded-xl border border-slate-100 bg-slate-50 px-4 py-3">
                <MapPin size={17} className="mt-0.5 shrink-0 text-indigo-600" />
                <div className="min-w-0 text-sm text-slate-800">
                  <p className="break-words font-medium">{endereco.primary}</p>
                  {endereco.secondary && (
                    <p className="mt-1 break-words text-xs text-slate-500">{endereco.secondary}</p>
                  )}
                </div>
              </div>
            </section>
          )}

          <section>
            <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">Itens do pedido</p>
            <div className="divide-y divide-slate-100">
              {itens.length === 0 ? (
                <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 px-4 py-5 text-sm text-slate-500">
                  Nenhum item encontrado para este pedido.
                </div>
              ) : (
                itens.map((item, index) => {
                  const complements = [
                    ...(item.subItens || []),
                    ...(item.adicionais || []),
                  ].map((sub) => sub.nome || sub.descricao).filter(Boolean);
                  const itemTotal = getItemTotal(item);
                  return (
                    <div key={`${pedido.id}-item-${index}`} className="flex items-start justify-between gap-4 py-3">
                      <div className="flex min-w-0 items-start gap-3">
                        <span className="flex h-8 min-w-8 items-center justify-center rounded-lg bg-slate-100 px-2 text-xs font-semibold text-indigo-600">
                          {getItemQuantity(item)}x
                        </span>
                        <div className="min-w-0">
                          <p className="break-words text-sm font-semibold text-slate-900">{getItemName(item)}</p>
                          {complements.length > 0 && (
                            <p className="mt-1 break-words text-xs text-slate-500">
                              {complements.join(' · ')}
                            </p>
                          )}
                          {item.observacao && (
                            <p className="mt-1 break-words text-xs text-slate-500">Observação: {item.observacao}</p>
                          )}
                        </div>
                      </div>
                      <span className="shrink-0 text-sm font-semibold text-slate-900">
                        {itemTotal === null ? '-' : formatCurrencyFromNumber(itemTotal)}
                      </span>
                    </div>
                  );
                })
              )}
            </div>

            <div className="mt-3 rounded-xl border border-slate-100 bg-slate-50 px-4 py-3">
              {toNumber(desconto) !== null && (
                <div className="flex items-center justify-between py-1 text-sm text-slate-600">
                  <span>Desconto</span>
                  <span>- {formatCurrency(desconto)}</span>
                </div>
              )}
              {toNumber(taxaEntrega) !== null && (
                <div className="flex items-center justify-between py-1 text-sm text-slate-600">
                  <span>Taxa de entrega</span>
                  <span>{formatCurrency(taxaEntrega)}</span>
                </div>
              )}
              {toNumber(troco) !== null && (
                <div className="flex items-center justify-between py-1 text-sm text-slate-600">
                  <span>Troco para</span>
                  <span>{formatCurrency(troco)}</span>
                </div>
              )}
              <div className="mt-2 flex items-center justify-between border-t border-slate-200 pt-3">
                <span className="text-sm font-semibold text-slate-700">Total do pedido</span>
                <span className="text-lg font-semibold text-slate-900">{formatCurrency(total)}</span>
              </div>
            </div>
          </section>

          {(pedido?.pedido?.descricao || pedido?.pedido?.observacao || pedido?.pedido?.cancelamento_motivo) && (
            <section className="rounded-xl border border-slate-100 bg-slate-50 px-4 py-3">
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">Observações</p>
              {pedido?.pedido?.descricao && (
                <p className="mt-2 whitespace-pre-wrap text-sm text-slate-700">{pedido.pedido.descricao}</p>
              )}
              {pedido?.pedido?.observacao && (
                <p className="mt-2 whitespace-pre-wrap text-sm text-slate-700">{pedido.pedido.observacao}</p>
              )}
              {pedido?.pedido?.cancelamento_motivo && (
                <p className="mt-2 whitespace-pre-wrap text-sm text-rose-700">
                  Motivo do cancelamento: {pedido.pedido.cancelamento_motivo}
                </p>
              )}
            </section>
          )}
        </div>
      </div>
    </div>
  );
}

const OrderHistory = () => {
  const [pedidos, setPedidos] = useState<Pedido[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isExportingPdf, setIsExportingPdf] = useState(false);
  const [feedbackError, setFeedbackError] = useState('');
  const [search, setSearch] = useState('');
  const [dateStart, setDateStart] = useState(getCurrentMonthStart());
  const [dateEnd, setDateEnd] = useState(getTodayDateStr());
  const [timeStart, setTimeStart] = useState('00:00');
  const [timeEnd, setTimeEnd] = useState('23:59');
  const [paymentFilter, setPaymentFilter] = useState('all');
  const [deliveryFilter, setDeliveryFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedPedidoId, setSelectedPedidoId] = useState<string | null>(null);

  const fetchPedidos = useCallback(async () => {
    setIsLoading(true);
    setFeedbackError('');

    try {
      const { data } = await api.get('/atendabot/pedidos', {
        params: { dateStart, dateEnd },
      });
      setPedidos(Array.isArray(data) ? data : []);
    } catch (error: any) {
      setPedidos([]);
      setFeedbackError(getApiErrorMessage(error, 'Erro ao carregar o histórico de pedidos.'));
    } finally {
      setIsLoading(false);
    }
  }, [dateStart, dateEnd]);

  useEffect(() => {
    void fetchPedidos();
  }, [fetchPedidos]);

  const pedidosNormalizados = useMemo<PedidoWithStatus[]>(() => (
    pedidos.map((pedido) => ({
      ...pedido,
      kanbanStatus: normalizeStatus(pedido.status),
    }))
  ), [pedidos]);

  const pedidosFiltrados = useMemo(() => {
    const termo = normalizeText(search);
    const termoDigitos = search.replace(/\D/g, '');
    const startMinutes = timeInputToMinutes(timeStart);
    const endMinutes = timeInputToMinutes(timeEnd);

    return pedidosNormalizados.filter((pedido) => {
      if (termo) {
        const searchable = getSearchText(pedido);
        const phoneDigits = getClienteTelefone(pedido).replace(/\D/g, '');
        const matchesText = searchable.includes(termo);
        const matchesDigits = termoDigitos ? phoneDigits.includes(termoDigitos) : false;
        if (!matchesText && !matchesDigits) return false;
      }

      if (paymentFilter !== 'all' && getPaymentFilterKey(pedido) !== paymentFilter) return false;
      if (deliveryFilter !== 'all' && getDeliveryType(pedido) !== deliveryFilter) return false;
      if (statusFilter !== 'all' && pedido.kanbanStatus !== statusFilter) return false;

      const pedidoMinutes = getTimeMinutes(pedido.criado_em);
      if (pedidoMinutes !== null && startMinutes !== null && pedidoMinutes < startMinutes) return false;
      if (pedidoMinutes !== null && endMinutes !== null && pedidoMinutes > endMinutes) return false;

      return true;
    });
  }, [
    pedidosNormalizados,
    search,
    timeStart,
    timeEnd,
    paymentFilter,
    deliveryFilter,
    statusFilter,
  ]);

  useEffect(() => {
    setCurrentPage(1);
  }, [search, timeStart, timeEnd, paymentFilter, deliveryFilter, statusFilter, dateStart, dateEnd]);

  const summaryCards = useMemo<SummaryCard[]>(() => {
    const totalPedidos = pedidosFiltrados.length;
    const faturamento = pedidosFiltrados.reduce((total, pedido) => total + getPedidoTotalNumber(pedido), 0);
    const ticketMedio = totalPedidos > 0 ? faturamento / totalPedidos : 0;
    const delivery = pedidosFiltrados.filter((pedido) => getDeliveryType(pedido) === 'delivery').length;
    const retirada = pedidosFiltrados.filter((pedido) => getDeliveryType(pedido) === 'retirada').length;
    const cancelados = pedidosFiltrados.filter((pedido) => pedido.kanbanStatus === 'cancelado').length;

    return [
      { label: 'Total de pedidos', value: String(totalPedidos) },
      { label: 'Faturamento', value: formatCurrencyFromNumber(faturamento), valueClass: 'text-emerald-600' },
      { label: 'Ticket médio', value: formatCurrencyFromNumber(ticketMedio), valueClass: 'text-slate-900' },
      { label: 'Delivery', value: String(delivery), valueClass: 'text-blue-600' },
      { label: 'Retirada', value: String(retirada), valueClass: 'text-teal-600' },
      { label: 'Cancelados', value: String(cancelados), valueClass: 'text-amber-600' },
    ];
  }, [pedidosFiltrados]);

  const totalPages = Math.max(1, Math.ceil(pedidosFiltrados.length / PAGE_SIZE));

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const pedidosPaginados = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return pedidosFiltrados.slice(start, start + PAGE_SIZE);
  }, [pedidosFiltrados, currentPage]);

  const selectedPedido = useMemo(() => (
    pedidosNormalizados.find((pedido) => String(pedido.id) === selectedPedidoId) || null
  ), [pedidosNormalizados, selectedPedidoId]);

  const handleSubmitFilters = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    void fetchPedidos();
  };

  const handleClearFilters = () => {
    setSearch('');
    setDateStart(getCurrentMonthStart());
    setDateEnd(getTodayDateStr());
    setTimeStart('00:00');
    setTimeEnd('23:59');
    setPaymentFilter('all');
    setDeliveryFilter('all');
    setStatusFilter('all');
  };

  const buildExportRows = () => pedidosFiltrados.map((pedido) => {
    const date = getDateParts(pedido.criado_em);
    return {
      pedido: getPedidoDisplayId(pedido),
      data: date.date,
      hora: date.time,
      cliente: getClienteNome(pedido),
      telefone: formatPhone(getClienteTelefone(pedido)),
      itens: getPedidoItemsCount(pedido),
      total: formatCurrencyFromNumber(getPedidoTotalNumber(pedido)),
      pagamento: getPaymentLabel(pedido),
      entrega: getTipoEntregaLabel(pedido),
      status: STATUS_LABELS[pedido.kanbanStatus],
    };
  });

  const handleExportCsv = () => {
    const rows = buildExportRows();
    const header = ['Pedido', 'Data', 'Hora', 'Cliente', 'Telefone', 'Itens', 'Total', 'Pagamento', 'Entrega', 'Status'];
    const csv = [
      header.map(buildCsvValue).join(';'),
      ...rows.map((row) => [
        row.pedido,
        row.data,
        row.hora,
        row.cliente,
        row.telefone,
        row.itens,
        row.total,
        row.pagamento,
        row.entrega,
        row.status,
      ].map(buildCsvValue).join(';')),
    ].join('\n');

    downloadFile('historico-pedidos-atendabot.csv', `\uFEFF${csv}`, 'text/csv;charset=utf-8');
  };

  const handleExportPdf = async () => {
    setIsExportingPdf(true);
    setFeedbackError('');

    try {
      const pdfModule = await import('jspdf');
      const JsPdfConstructor = (pdfModule as any).jsPDF || (pdfModule as any).default;
      const doc = new JsPdfConstructor({ orientation: 'landscape', unit: 'pt', format: 'a4' });
      const rows = buildExportRows();
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 36;
      const columns = [
        { label: 'Pedido', key: 'pedido', width: 58 },
        { label: 'Data', key: 'data', width: 62 },
        { label: 'Hora', key: 'hora', width: 48 },
        { label: 'Cliente', key: 'cliente', width: 150 },
        { label: 'Itens', key: 'itens', width: 42 },
        { label: 'Total', key: 'total', width: 72 },
        { label: 'Pagamento', key: 'pagamento', width: 78 },
        { label: 'Entrega', key: 'entrega', width: 72 },
        { label: 'Status', key: 'status', width: 82 },
      ];

      const drawHeader = () => {
        doc.setFontSize(16);
        doc.text('Histórico de pedidos do Atendabot', margin, 34);
        doc.setFontSize(9);
        doc.setTextColor(110);
        doc.text(`${rows.length} pedidos exportados`, margin, 50);
        doc.setTextColor(0);
      };

      const drawTableHeader = (y: number) => {
        let x = margin;
        doc.setFillColor(245, 247, 251);
        doc.rect(margin, y - 14, pageWidth - margin * 2, 22, 'F');
        doc.setFontSize(8);
        doc.setTextColor(90);
        columns.forEach((column) => {
          doc.text(column.label, x + 4, y);
          x += column.width;
        });
        doc.setTextColor(0);
      };

      drawHeader();
      let y = 82;
      drawTableHeader(y);
      y += 24;
      doc.setFontSize(8);

      rows.forEach((row) => {
        if (y > pageHeight - 42) {
          doc.addPage();
          drawHeader();
          y = 82;
          drawTableHeader(y);
          y += 24;
          doc.setFontSize(8);
        }

        let x = margin;
        columns.forEach((column) => {
          const value = String((row as any)[column.key] ?? '-');
          const text = doc.splitTextToSize(value, column.width - 8)[0] || '';
          doc.text(text, x + 4, y);
          x += column.width;
        });
        y += 18;
      });

      doc.save('historico-pedidos-atendabot.pdf');
    } catch (error: any) {
      setFeedbackError(getApiErrorMessage(error, 'Não foi possível gerar o PDF agora.'));
    } finally {
      setIsExportingPdf(false);
    }
  };

  const paginationPages = useMemo(() => {
    const pages = new Set<number>([1, totalPages, currentPage]);
    if (currentPage > 1) pages.add(currentPage - 1);
    if (currentPage < totalPages) pages.add(currentPage + 1);
    return Array.from(pages).filter((page) => page >= 1 && page <= totalPages).sort((a, b) => a - b);
  }, [currentPage, totalPages]);

  return (
    <Layout>
      <div className="min-h-full bg-[#f6f7fb] px-3 py-6 text-slate-900 sm:px-6 lg:px-8">
        <div className="mx-auto w-full max-w-[1400px] space-y-5">
          <div className="text-center">
            <h2 className="flex items-center justify-center gap-3 text-2xl font-black text-slate-900">
              <FileText size={28} className="text-[#2f7d72]" />
              Histórico de pedidos
            </h2>
            <p className="mx-auto mt-3 max-w-3xl text-sm leading-6 text-slate-600">
              Consulte, filtre e exporte todos os pedidos recebidos pelo Atendabot.
            </p>
          </div>

          <form
            className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm md:p-5"
            onSubmit={handleSubmitFilters}
          >
            <div className="grid gap-3 xl:grid-cols-[2fr_repeat(5,1fr)]">
              <label className="flex flex-col gap-1">
                <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Buscar</span>
                <div className="relative">
                  <Search size={15} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    type="text"
                    className="h-10 w-full rounded-lg border border-slate-200 bg-slate-50 py-2 pl-9 pr-3 text-sm outline-none transition focus:border-teal-300 focus:ring-2 focus:ring-teal-100"
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    placeholder="Cliente, telefone, item ou ID"
                  />
                </div>
              </label>

              <label className="flex flex-col gap-1">
                <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Data início</span>
                <input
                  type="date"
                  className="h-10 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm outline-none transition focus:border-teal-300 focus:ring-2 focus:ring-teal-100"
                  value={dateStart}
                  onChange={(event) => setDateStart(event.target.value)}
                  max={dateEnd}
                />
              </label>

              <label className="flex flex-col gap-1">
                <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Data fim</span>
                <input
                  type="date"
                  className="h-10 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm outline-none transition focus:border-teal-300 focus:ring-2 focus:ring-teal-100"
                  value={dateEnd}
                  onChange={(event) => setDateEnd(event.target.value)}
                  min={dateStart}
                />
              </label>

              <label className="flex flex-col gap-1">
                <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Horário de</span>
                <input
                  type="time"
                  className="h-10 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm outline-none transition focus:border-teal-300 focus:ring-2 focus:ring-teal-100"
                  value={timeStart}
                  onChange={(event) => setTimeStart(event.target.value)}
                />
              </label>

              <label className="flex flex-col gap-1">
                <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Horário até</span>
                <input
                  type="time"
                  className="h-10 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm outline-none transition focus:border-teal-300 focus:ring-2 focus:ring-teal-100"
                  value={timeEnd}
                  onChange={(event) => setTimeEnd(event.target.value)}
                />
              </label>

              <label className="flex flex-col gap-1">
                <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Pagamento</span>
                <select
                  className="h-10 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm outline-none transition focus:border-teal-300 focus:ring-2 focus:ring-teal-100"
                  value={paymentFilter}
                  onChange={(event) => setPaymentFilter(event.target.value)}
                >
                  {PAYMENT_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </label>
            </div>

            <div className="mt-3 grid gap-3 md:grid-cols-[1fr_1fr_1fr_auto_auto]">
              <label className="flex flex-col gap-1">
                <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Entrega</span>
                <select
                  className="h-10 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm outline-none transition focus:border-teal-300 focus:ring-2 focus:ring-teal-100"
                  value={deliveryFilter}
                  onChange={(event) => setDeliveryFilter(event.target.value)}
                >
                  <option value="all">Todos</option>
                  <option value="delivery">Delivery</option>
                  <option value="retirada">Retirada</option>
                </select>
              </label>

              <label className="flex flex-col gap-1">
                <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Status</span>
                <select
                  className="h-10 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm outline-none transition focus:border-teal-300 focus:ring-2 focus:ring-teal-100"
                  value={statusFilter}
                  onChange={(event) => setStatusFilter(event.target.value)}
                >
                  {STATUS_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </label>

              <div className="hidden md:block" />

              <button
                type="submit"
                className="inline-flex h-10 items-center justify-center gap-2 rounded-lg bg-[#2f7d72] px-4 text-sm font-semibold text-white transition hover:bg-[#27685f] disabled:cursor-not-allowed disabled:opacity-60"
                disabled={isLoading}
              >
                {isLoading ? <Loader2 size={15} className="animate-spin" /> : <Search size={15} />}
                Filtrar
              </button>

              <button
                type="button"
                onClick={handleClearFilters}
                className="inline-flex h-10 items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-600 transition hover:bg-slate-50"
              >
                <RotateCcw size={15} />
                Limpar
              </button>
            </div>
          </form>

          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
            {summaryCards.map((card) => (
              <div key={card.label} className="rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
                <p className="text-xs font-medium text-slate-500">{card.label}</p>
                <p className={`mt-2 truncate text-2xl font-semibold ${card.valueClass || 'text-slate-900'}`}>
                  {card.value}
                </p>
              </div>
            ))}
          </div>

          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm text-slate-500">
              {pedidosFiltrados.length} {pedidosFiltrados.length === 1 ? 'pedido encontrado' : 'pedidos encontrados'}
            </p>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={handleExportCsv}
                className="inline-flex h-9 items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-600 transition hover:bg-slate-50"
                disabled={pedidosFiltrados.length === 0}
              >
                <Download size={15} />
                Exportar CSV
              </button>
              <button
                type="button"
                onClick={handleExportPdf}
                className="inline-flex h-9 items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-600 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
                disabled={pedidosFiltrados.length === 0 || isExportingPdf}
              >
                {isExportingPdf ? <Loader2 size={15} className="animate-spin" /> : <FileText size={15} />}
                Exportar PDF
              </button>
            </div>
          </div>

          {feedbackError && (
            <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
              {feedbackError}
            </div>
          )}

          <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
            <div className="overflow-x-auto">
              <table className="min-w-[1100px] w-full table-fixed border-collapse text-sm">
                <thead>
                  <tr className="bg-slate-50 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                    <th className="w-[150px] px-4 py-3">Pedido</th>
                    <th className="w-[112px] px-4 py-3">Data</th>
                    <th className="w-[72px] px-4 py-3">Hora</th>
                    <th className="w-[160px] px-4 py-3">Cliente</th>
                    <th className="w-[70px] px-4 py-3">Itens</th>
                    <th className="w-[104px] px-4 py-3">Total</th>
                    <th className="w-[108px] px-4 py-3">Pagamento</th>
                    <th className="w-[112px] px-4 py-3">Entrega</th>
                    <th className="w-[148px] px-4 py-3">Status</th>
                    <th className="w-[76px] px-4 py-3 text-right">Ações</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {isLoading ? (
                    <tr>
                      <td colSpan={10} className="px-4 py-16 text-center text-slate-400">
                        <span className="inline-flex items-center gap-2">
                          <Loader2 size={18} className="animate-spin" />
                          Carregando pedidos...
                        </span>
                      </td>
                    </tr>
                  ) : pedidosPaginados.length === 0 ? (
                    <tr>
                      <td colSpan={10} className="px-4 py-16 text-center text-slate-400">
                        Nenhum pedido encontrado com os filtros selecionados.
                      </td>
                    </tr>
                  ) : (
                    pedidosPaginados.map((pedido) => {
                      const date = getDateParts(pedido.criado_em);
                      const pedidoId = getPedidoDisplayId(pedido);
                      const deliveryType = getDeliveryType(pedido);
                      return (
                        <tr
                          key={String(pedido.id)}
                          className="cursor-pointer transition hover:bg-slate-50"
                          onClick={() => setSelectedPedidoId(String(pedido.id))}
                        >
                          <td className="whitespace-nowrap px-4 py-3 font-medium text-slate-500">
                            {pedidoId ? `#${pedidoId}` : '-'}
                          </td>
                          <td className="whitespace-nowrap px-4 py-3 text-slate-500">{date.date}</td>
                          <td className="whitespace-nowrap px-4 py-3 text-slate-500">{date.time}</td>
                          <td className="min-w-0 px-4 py-3">
                            <p className="truncate text-sm font-semibold text-slate-900">{getClienteNome(pedido)}</p>
                            <p className="truncate text-xs text-slate-400">{formatPhone(getClienteTelefone(pedido))}</p>
                          </td>
                          <td className="px-4 py-3 text-slate-500">{getPedidoItemsCount(pedido)}</td>
                          <td className="truncate px-4 py-3 font-semibold text-slate-900">
                            {formatCurrencyFromNumber(getPedidoTotalNumber(pedido))}
                          </td>
                          <td className="truncate px-4 py-3 text-slate-700">{getPaymentLabel(pedido)}</td>
                          <td className="px-4 py-3">
                            <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${DELIVERY_BADGE_CLASSES[deliveryType]}`}>
                              {getTipoEntregaLabel(pedido)}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${STATUS_BADGE_CLASSES[pedido.kanbanStatus]}`}>
                              {STATUS_LABELS[pedido.kanbanStatus]}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-right">
                            <button
                              type="button"
                              onClick={(event) => {
                                event.stopPropagation();
                                setSelectedPedidoId(String(pedido.id));
                              }}
                              className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 transition hover:bg-slate-50"
                              aria-label="Ver detalhes do pedido"
                              title="Ver detalhes"
                            >
                              <Eye size={16} />
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            <div className="flex flex-col gap-3 border-t border-slate-100 bg-slate-50 px-4 py-3 text-sm text-slate-500 sm:flex-row sm:items-center sm:justify-between">
              <span>
                Página {currentPage} de {totalPages} - {pedidosFiltrados.length} pedidos
              </span>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  className="rounded-md border border-slate-200 bg-white px-3 py-1.5 font-semibold text-slate-600 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50"
                  onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
                  disabled={currentPage === 1}
                >
                  Anterior
                </button>
                {paginationPages.map((page, index) => {
                  const previous = paginationPages[index - 1];
                  const showGap = previous && page - previous > 1;
                  return (
                    <React.Fragment key={page}>
                      {showGap && <span className="px-1 text-slate-300">...</span>}
                      <button
                        type="button"
                        className={`rounded-md border px-3 py-1.5 font-semibold transition ${
                          currentPage === page
                            ? 'border-teal-600 bg-[#2f7d72] text-white'
                            : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-100'
                        }`}
                        onClick={() => setCurrentPage(page)}
                      >
                        {page}
                      </button>
                    </React.Fragment>
                  );
                })}
                <button
                  type="button"
                  className="rounded-md border border-slate-200 bg-white px-3 py-1.5 font-semibold text-slate-600 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50"
                  onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}
                  disabled={currentPage === totalPages}
                >
                  Próxima
                </button>
              </div>
            </div>
          </section>
        </div>
      </div>

      {selectedPedido && (
        <PedidoDetailModal
          pedido={selectedPedido}
          onClose={() => setSelectedPedidoId(null)}
        />
      )}
    </Layout>
  );
};

export default OrderHistory;
